import { useState } from 'react'

import { Box, Container, Switch, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import api from 'common/api'
import useSnackbar from 'common/use-snackbar'
import VisitsNotifications from 'components/VisitsNotifications'
import { useAppDispatch } from 'store'
import {
  CadenceTypes,
  EmailFrequency,
  EmailFrequencyType,
  TargetHighSchoolVisitCreation,
  actions as userActions,
} from 'store/user'
import { useUser } from 'store/user/hooks'

const useStyles = makeStyles((theme) => ({
  settingToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(3),
  },
  settingSection: {
    padding: theme.spacing(3, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}))

const EmailNotificationsContent = ({
  canManageDocuments,
  canViewHandraisers,
  canReceiveTargetHighSchoolEmails,
}: {
  canManageDocuments: boolean
  canViewHandraisers: boolean
  canReceiveTargetHighSchoolEmails: boolean
}) => {
  const classes = useStyles()
  const user = useUser()
  const dispatch = useAppDispatch()
  const setSnackbarError = useSnackbar('An error occurred while updating your email preferences.')

  /** Document download alerts */
  const [isEmailOptIn, setIsEmailOptIn] = useState(user?.NotificationPreference?.EmailOptIn)
  const handleEmailOptInChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isToggled = event.target.checked
    setIsEmailOptIn(isToggled)
    updateEmailPreference(isToggled)
  }

  const updateEmailPreference = (isToggled: boolean) => {
    const path = '/api/user/emailoptin'
    const request = isToggled ? api.put(path) : api.delete(path)
    request
      .then(() => dispatch(userActions.fetchUser()))
      .catch(() => {
        // Revert toggle if request fails
        setIsEmailOptIn(!isToggled)
        setSnackbarError()
      })
  }

  /** Handraiser download alerts */
  const [isHandraisersEmailOptIn, setIsHandraisersEmailOptIn] = useState(
    user?.NotificationPreference?.HandraisersEmailOptIn,
  )
  const handleHandraisersEmailOptInChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isToggled = event.target.checked
    setIsHandraisersEmailOptIn(isToggled)
    updateHandraisersEmailPreference(isToggled)
  }

  const updateHandraisersEmailPreference = (isToggled: boolean) => {
    const path = '/api/user/handraisersEmailOptIn'
    const request = isToggled ? api.put(path) : api.delete(path)
    request
      .then(() => dispatch(userActions.fetchUser()))
      .catch(() => {
        // Revert toggle if request fails
        setIsHandraisersEmailOptIn(!isToggled)
        setSnackbarError()
      })
  }

  /** Visits notifications */
  const persistedFrequency = user?.NotificationPreference?.Visits?.SourceFrequencies?.[TargetHighSchoolVisitCreation]
  const isPersistedActive = persistedFrequency !== undefined && persistedFrequency !== EmailFrequency.Off
  const persistedCadence: CadenceTypes = isPersistedActive ? persistedFrequency : EmailFrequency.Immediately

  const [cadencePreference, setCadencePreference] = useState<CadenceTypes>(persistedCadence)
  const [enableVisitsNotifications, setEnableVisitsNotifications] = useState(isPersistedActive)

  const handleCadenceChange = (newPreference: CadenceTypes, enabled: boolean) => {
    const payload: { PreferenceType: EmailFrequencyType } = {
      PreferenceType: enabled ? newPreference : EmailFrequency.Off,
    }

    api
      .put('/api/user/visitsNotificationsPreference', payload)
      .then(() => {
        setCadencePreference(newPreference)
        dispatch(userActions.fetchUser())
      })
      .catch(() => {
        setCadencePreference(persistedCadence)
        setEnableVisitsNotifications(isPersistedActive)
        setSnackbarError()
      })
  }

  if (!user) {
    return null
  }

  return (
    <Container maxWidth="lg">
      <div>
        <Typography
          variant="h6"
          component="h1"
        >
          Email Notifications
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
        >
          {user.Email}
        </Typography>
      </div>
      {canManageDocuments && (
        <Box className={`${classes.settingSection} ${classes.settingToggle}`}>
          <div>
            <Typography>
              <b>Document download alerts</b>
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
            >
              Receive an email when new documents are ready to download.
            </Typography>
          </div>
          <Switch
            color="primary"
            checked={isEmailOptIn || false}
            onChange={handleEmailOptInChange}
          />
        </Box>
      )}
      {canViewHandraisers && (
        <Box className={`${classes.settingSection} ${classes.settingToggle}`}>
          <div>
            <Typography>
              <b>Handraiser download alerts</b>
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
            >
              Receive an email when new handraiser files are ready to download.
            </Typography>
          </div>
          <Switch
            color="primary"
            checked={isHandraisersEmailOptIn || false}
            onChange={handleHandraisersEmailOptInChange}
          />
        </Box>
      )}
      {canReceiveTargetHighSchoolEmails && (
        <Box className={classes.settingSection}>
          <Box className={classes.settingToggle}>
            <div>
              <Typography>
                <b>New visit availability alerts</b>
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
              >
                Get notified when new high school visits are open for scheduling.
              </Typography>
            </div>
            <Switch
              color="primary"
              checked={enableVisitsNotifications}
              onChange={(_, checked) => {
                setEnableVisitsNotifications(checked)
                handleCadenceChange(cadencePreference, checked)
              }}
            />
          </Box>
          <VisitsNotifications
            disabled={!enableVisitsNotifications}
            selectedCadence={cadencePreference}
            onCadenceChange={(cadencePreference: CadenceTypes) =>
              handleCadenceChange(cadencePreference, enableVisitsNotifications)
            }
          />
        </Box>
      )}
    </Container>
  )
}

export default EmailNotificationsContent
